import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import BbtiConfigurationForm from "./BbtiConfigurationForm";
import style from "./bms-config.scss";
import { Brand, Component, ComponentFeature, ComponentFeatures, Test } from "./BmsCommonInterfaces";
import {
    AvailablePlatforms,
    getAppImageAndColours,
    getAppStoreUrl,
    getAvailableApps,
    getComplementaryColor,
    getFeatureName,
    getPlatformTypes,
    MAX_APP_NAME_LENGTH,
} from "./bmsUtils";
import DiagnosticsTestsForm from "./diagnostics/DiagnosticsTestsForm";
import Checkbox from "./form-components/BmsCheckboxComponent";
import BmsOptionsInputComponent from "./form-components/BmsOptionsInputComponent";
import RadioButton from "./form-components/BmsRadioButtonComponent";
import BmsSectionHeader from "./form-components/BmsSectionHeader";
import InputComponent from "./form-components/BmsTextInputComponent";
import IdentityVerificationConfigurationForm from "./IdentityVerificationConfigurationForm";
import InsuranceConfigurationForm from "./InsuranceConfigurationForm";
import LeaseConfigurationForm from "./LeaseConfigurationForm";
import NtfConfigurationForm from "./NtfConfigurationForm";
import ValidationConfigurationForm from "./ValidationConfigurationForm";
import { BMS_LICENSE_FEATURE_GATHER_IMEI, BMS_LICENSE_FEATURE_LEASE } from "components/licenses/common";
import { Configuration } from "domain/licenses";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";

interface Props {
    configuration: Configuration;
    setFormValues: React.Dispatch<React.SetStateAction<Configuration>>;
    onSave?: () => void;
    allowTestSelection?: boolean;
    allowAdvancedSettings?: boolean;
}
interface LimitCheck {
    failure_limit?: number;
    error_limit?: number;
    validity_period?: number;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});
const connector = connect(mapState);

const BmsWizard = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const platformType = getPlatformTypes();
    const availableApps = getAvailableApps();
    const [currentStep, setCurrentStep] = React.useState(0);
    const [showAdvanced, setShowAdvanced] = React.useState(false);
    const allBaseSteps = ["basicInfo", "appConfig", "colors", "features"];

    const tenantConfig = props.configuration?.tenantConfiguration || {};
    const brand: Brand = (tenantConfig.brand as Brand) || {};
    const componentFeatures: Record<string, ComponentFeature> =
        (tenantConfig.component_feature as Record<string, ComponentFeature>) || {};
    const limitCheck: LimitCheck = (tenantConfig.limit_check as LimitCheck) || {};
    const platform = (tenantConfig.platform as string) || "";
    const marketingNameUrl = (tenantConfig.marketing_name_url as string) || "";
    const selectedApp = (tenantConfig.selectedApp as string) || "";

    const isSDK = platform.toLowerCase() === "sdk";
    const baseSteps = isSDK ? allBaseSteps.filter((step) => step !== "features") : allBaseSteps;

    const excludedFromTests = ["gathering_imei", "Recommendations", "infogathering"];
    const enabledFeatures = props.allowTestSelection
        ? Object.entries(componentFeatures)
              .filter(([key, feature]: [string, ComponentFeature]) => {
                  if (!feature.enabled) {
                      return false;
                  }
                  if (excludedFromTests.includes(key)) {
                      return false;
                  }
                  return true;
              })
              .map(([key]) => key)
        : [];

    const steps = [...baseSteps, ...enabledFeatures.map((feature) => `${feature}Tests`)];
    const stepLabels = [...enabledFeatures.map((feature) => `${feature} Tests`)];
    const featureNames = React.useMemo(() => {
        const names: Record<string, string> = {};
        enabledFeatures.forEach((feature) => {
            names[feature] = getFeatureName(feature, t);
        });
        return names;
    }, [enabledFeatures, t]);

    const singleComponentSelectionFeatures = [BMS_LICENSE_FEATURE_GATHER_IMEI, BMS_LICENSE_FEATURE_LEASE];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const COLOR_FIELDS = ["primaryColor", "secondaryColor", "tertiaryColor"];

    const handleInputChange = (fieldName: string, value: string) => {
        const updates: Configuration = {
            ...props.configuration,
            tenantConfiguration: {
                ...(props.configuration?.tenantConfiguration || {}),
                brand: {
                    ...(props.configuration?.tenantConfiguration?.brand || {}),
                    [fieldName]: fieldName === "checkContrast" ? value === "true" : value,
                },
            },
        };

        if (COLOR_FIELDS.includes(fieldName)) {
            updates.downloadAppPageConfiguration = {
                ...(props.configuration?.downloadAppPageConfiguration || {}),
                [fieldName]: value,
            };
        }

        props.setFormValues(updates);
    };

    const handlePlatformChange = (value: string) => {
        const updates: Configuration = {
            ...props.configuration,
            tenantConfiguration: {
                ...tenantConfig,
                platform: value,
            },
        };

        if (value === AvailablePlatforms.BROWSER) {
            const browserUrls = getAppStoreUrl(value);
            updates.downloadAppPageConfiguration = {
                ...(props.configuration?.downloadAppPageConfiguration || {}),
                androidFirebaseLink: browserUrls.androidLink,
                iOSFirebaseLink: browserUrls.iosLink,
                autoForward: browserUrls.autoForward,
            };
        }

        props.setFormValues(updates);
    };

    const handleImageLimitChange = (fieldName: string, value: string) => {
        props.setFormValues({
            ...props.configuration,
            tenantConfiguration: {
                ...(props.configuration?.tenantConfiguration || {}),
                limit_check: {
                    ...(props.configuration?.tenantConfiguration?.limit_check || {}),
                    [fieldName]: value ? parseInt(value) : undefined,
                },
            },
        });
    };

    const handleAppSelection = (value: string) => {
        const appUrls = getAppStoreUrl(value);
        const updates: Configuration = {
            ...props.configuration,
            tenantConfiguration: {
                ...tenantConfig,
                selectedApp: value,
            },
            downloadAppPageConfiguration: {
                ...(props.configuration?.downloadAppPageConfiguration || {}),
                androidFirebaseLink: appUrls.androidLink,
                iOSFirebaseLink: appUrls.iosLink,
                autoForward: appUrls.autoForward,
            },
        };

        // Update splash screen image based on app selection
        const currentDomain = window.location.origin;
        const currentImage = brand.image || "";

        if (currentImage) {
            const appImages = getAppImageAndColours();
            const appImage = appImages.find((img) => img.value === value);
            if (appImage) {
                updates.tenantConfiguration = {
                    ...updates.tenantConfiguration,
                    brand: {
                        ...brand,
                        image: `${currentDomain}${appImage.path || ""}`,
                        primaryColor: appImage.primaryColor,
                        secondaryColor: appImage.secondaryColor,
                        tertiaryColor: appImage.tertiaryColor,
                        checkContrast: appImage.checkContrast,
                    },
                };
            }
        }

        props.setFormValues(updates);
    };

    const handleMarketingNameUrlChange = (value: string) => {
        props.setFormValues({
            ...props.configuration,
            tenantConfiguration: {
                ...tenantConfig,
                marketing_name_url: value,
            },
        });
    };

    const handleComponentChange = (featureKey: string, componentName: string | null, isChecked: boolean) => {
        props.setFormValues((currentConfig) => {
            const updatedConfig = { ...currentConfig };
            const componentFeatures =
                (updatedConfig.tenantConfiguration?.component_feature as Record<string, ComponentFeature>) || {};
            const feature = componentFeatures[featureKey];

            if (feature) {
                if (singleComponentSelectionFeatures.includes(featureKey) && componentName != null) {
                    feature.components.forEach((component: Component) => {
                        if (component.name.toLowerCase() !== componentName.toLowerCase()) {
                            component.enabled = false;
                        }
                    });
                }

                if (componentName === null) {
                    feature.enabled = isChecked;
                    if (!isChecked) {
                        feature.components.forEach((component: Component) => {
                            component.enabled = false;
                        });
                    }

                    // Auto-select linked features
                    if (isChecked && (featureKey === "FaultCheck" || featureKey === "Recommendations")) {
                        const linkedFeature = featureKey === "FaultCheck" ? "Recommendations" : "FaultCheck";
                        const feature = componentFeatures[linkedFeature];
                        if (feature) {
                            feature.enabled = true;
                        }
                    }
                } else {
                    const component = feature.components.find((c: Component) => c.name === componentName);
                    if (component) {
                        component.enabled = isChecked;
                    }
                }
            }

            return updatedConfig;
        });
    };

    const getSectionHeader = () => {
        const currentField = steps[currentStep];
        if (currentField === "appConfig") {
            return {
                labelKey: t("Configuration.genericConfigurationForm.headers.appConfiguration"),
                toolTipContent: t("Configuration.tooltips.genericConfigurationForm.appConfiguration"),
            };
        }
        if (currentField === "features") {
            return {
                labelKey: t("Configuration.genericConfigurationForm.headers.features"),
                toolTipContent: t("Configuration.tooltips.genericConfigurationForm.featureSection"),
            };
        }
        if (currentField.endsWith("Tests")) {
            const featureKey = currentField.replace("Tests", "");
            return {
                labelKey: featureNames[featureKey] || `${featureKey} Tests`,
            };
        }
        return {
            labelKey: t("Configuration.genericConfigurationForm.headers.brand"),
            toolTipContent: t("Configuration.tooltips.genericConfigurationForm.brand"),
        };
    };

    const hasEnabledFeatures = Object.values(componentFeatures).some((feature: ComponentFeature) => feature.enabled);
    const canSave = isSDK || hasEnabledFeatures;

    const renderCurrentStep = () => {
        const currentField = steps[currentStep];

        switch (currentField) {
            case "basicInfo":
                return (
                    <>
                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.appName")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.appName", {
                                max_length: MAX_APP_NAME_LENGTH,
                            })}
                            inputId={"appName"}
                            value={brand.appName || ""}
                            onChange={(newValue) => handleInputChange("appName", newValue)}
                            maxLength={MAX_APP_NAME_LENGTH}
                        />
                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.tagLine")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.tagLine")}
                            inputId={"tag_line"}
                            value={brand.tag_line || ""}
                            onChange={(newValue) => handleInputChange("tag_line", newValue)}
                        />
                    </>
                );
            case "colors":
                return (
                    <>
                        <div className={classNames(style.inputContainer)}>
                            <label className={classNames(style.bmsLabel)}>
                                {t("Configuration.genericConfigurationForm.primaryColor")}
                            </label>
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <input
                                    type="color"
                                    value={brand.primaryColor || "#000000"}
                                    onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                                    className={classNames(style.colorPickerIcon)}
                                />
                                <input
                                    type="text"
                                    value={brand.primaryColor || ""}
                                    onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                                    className={classNames(style.brandInput)}
                                    style={{
                                        backgroundColor: brand.primaryColor || "",
                                        color: getComplementaryColor(brand.tertiaryColor || ""),
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classNames(style.inputContainer)}>
                            <label className={classNames(style.bmsLabel)}>
                                {t("Configuration.genericConfigurationForm.secondaryColor")}
                            </label>
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <input
                                    type="color"
                                    value={brand.secondaryColor || "#000000"}
                                    onChange={(e) => handleInputChange("secondaryColor", e.target.value)}
                                    className={classNames(style.colorPickerIcon)}
                                />
                                <input
                                    type="text"
                                    value={brand.secondaryColor || ""}
                                    onChange={(e) => handleInputChange("secondaryColor", e.target.value)}
                                    className={classNames(style.brandInput)}
                                    style={{
                                        backgroundColor: brand.secondaryColor || "",
                                        color: getComplementaryColor(brand.tertiaryColor || ""),
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classNames(style.inputContainer)}>
                            <label className={classNames(style.bmsLabel)}>
                                {t("Configuration.genericConfigurationForm.tertiaryColor")}
                            </label>
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <input
                                    type="color"
                                    value={brand.tertiaryColor || "#000000"}
                                    onChange={(e) => handleInputChange("tertiaryColor", e.target.value)}
                                    className={classNames(style.colorPickerIcon)}
                                />
                                <input
                                    type="text"
                                    value={brand.tertiaryColor || ""}
                                    onChange={(e) => handleInputChange("tertiaryColor", e.target.value)}
                                    className={classNames(style.brandInput)}
                                    style={{
                                        backgroundColor: brand.tertiaryColor || "",
                                        color: getComplementaryColor(brand.tertiaryColor || ""),
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classNames(style.nomargin)}>
                            <Checkbox
                                labelKey={t("Configuration.genericConfigurationForm.checkContrast")}
                                toolTipContent={t("Configuration.tooltips.genericConfigurationForm.checkContrast")}
                                checkboxId={"checkContrast"}
                                checked={brand.checkContrast || false}
                                onChange={(newValue: boolean) => handleInputChange("checkContrast", newValue + "")}
                            />
                        </div>
                    </>
                );
            case "appConfig":
                return (
                    <>
                        {platform.toLowerCase() !== "sdk" && (
                            <>
                                <InputComponent
                                    labelKey={t("Configuration.genericConfigurationForm.splashImage")}
                                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.splashImage")}
                                    inputId={"image"}
                                    value={brand.image || ""}
                                    onChange={(newValue) => handleInputChange("image", newValue)}
                                />
                                {brand.image && (
                                    <div className={classNames(style.inputContainer)}>
                                        <img
                                            src={brand.image}
                                            alt={t("AltText.logo")}
                                            className={classNames(style.displayImage)}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {showAdvanced && (
                            <InputComponent
                                labelKey={t("Configuration.genericConfigurationForm.marketingNameUrl")}
                                toolTipContent={t("Configuration.tooltips.genericConfigurationForm.marketingNameUrl")}
                                inputId={"marketing_name_url"}
                                value={marketingNameUrl}
                                onChange={(newValue) => handleMarketingNameUrlChange(newValue)}
                            />
                        )}

                        <BmsOptionsInputComponent
                            labelKey={t("Configuration.genericConfigurationForm.platformType")}
                            value={platform}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.platformType")}
                            options={platformType}
                            onChange={handlePlatformChange}
                        />

                        {(platform === AvailablePlatforms.APP_USDK || platform === AvailablePlatforms.APP_ISDK) && (
                            <BmsOptionsInputComponent
                                labelKey={t("Configuration.genericConfigurationForm.appSelection")}
                                value={selectedApp}
                                toolTipContent={t("Configuration.tooltips.genericConfigurationForm.appSelection")}
                                options={availableApps}
                                onChange={handleAppSelection}
                            />
                        )}

                        {showAdvanced && (
                            <div className={classNames(style.boxedSection)}>
                                <BmsSectionHeader
                                    labelKey={t("Configuration.genericConfigurationForm.headers.screenFraudPrevention")}
                                    toolTipContent={t(
                                        "Configuration.tooltips.genericConfigurationForm.screenFraudPrevention"
                                    )}
                                />

                                <InputComponent
                                    labelKey={t("Configuration.genericConfigurationForm.failureLimit")}
                                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.failureLimit")}
                                    inputId={"failureLimit"}
                                    inputType={"number"}
                                    value={limitCheck.failure_limit?.toString() || ""}
                                    onChange={(newValue) => handleImageLimitChange("failure_limit", newValue)}
                                />

                                <InputComponent
                                    labelKey={t("Configuration.genericConfigurationForm.errorLimit")}
                                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.errorLimit")}
                                    inputId={"errorLimit"}
                                    inputType={"number"}
                                    value={limitCheck.error_limit?.toString() || ""}
                                    onChange={(newValue) => handleImageLimitChange("error_limit", newValue)}
                                />

                                <InputComponent
                                    labelKey={t("Configuration.genericConfigurationForm.validityPeriod")}
                                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.validityPeriod")}
                                    inputId={"validityPeriod"}
                                    inputType={"number"}
                                    value={limitCheck.validity_period?.toString() || ""}
                                    onChange={(newValue) => handleImageLimitChange("validity_period", newValue)}
                                />
                            </div>
                        )}
                    </>
                );
            case "features":
                return (
                    <>
                        {Object.entries(componentFeatures).map(([featureKey, featureValue], index) => (
                            <div key={featureKey} className={classNames(style.featureItem)}>
                                <Checkbox
                                    labelKey={t(
                                        `Configuration.genericConfigurationForm.features.${featureKey.toLowerCase()}`
                                    )}
                                    toolTipContent={t(
                                        `Configuration.tooltips.genericConfigurationForm.features.${featureKey.toLowerCase()}`
                                    )}
                                    checkboxId={`feature-checkbox-${index}`}
                                    checked={featureValue.enabled || false}
                                    disabled={platform.toLowerCase() === "sdk"}
                                    name={featureKey}
                                    onChange={(newValue: boolean) => handleComponentChange(featureKey, null, newValue)}
                                />
                                {featureValue.enabled && (
                                    <>
                                        {(featureValue.components || []).map((component, componentIndex: number) => (
                                            <div
                                                key={`${featureKey}-${componentIndex}`}
                                                className={classNames(style.componentItem)}
                                            >
                                                {singleComponentSelectionFeatures.includes(featureKey) ? (
                                                    <RadioButton
                                                        labelKey={t(
                                                            `Configuration.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                        )}
                                                        toolTipContent={t(
                                                            `Configuration.tooltips.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                        )}
                                                        name={component.name}
                                                        radioId={`component-radio-${index}-${componentIndex}-${component.name}`}
                                                        checked={component.enabled || false}
                                                        onChange={(newValue: boolean) =>
                                                            handleComponentChange(featureKey, component.name, newValue)
                                                        }
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        labelKey={t(
                                                            `Configuration.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                        )}
                                                        toolTipContent={t(
                                                            `Configuration.tooltips.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                        )}
                                                        checkboxId={`component-checkbox-${index}-${componentIndex}`}
                                                        checked={component.enabled || false}
                                                        name={component.name}
                                                        onChange={(newValue: boolean) =>
                                                            handleComponentChange(featureKey, component.name, newValue)
                                                        }
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        ))}
                    </>
                );
            default:
                if (currentField.endsWith("Tests")) {
                    const featureKey = currentField.replace("Tests", "");
                    const getConfigurationTests = () => {
                        switch (featureKey) {
                            case "TradeIn":
                                return props.configuration?.bbtiConfiguration?.tests || [];
                            case "Lease":
                                return props.configuration?.leaseConfiguration?.tests || [];
                            case "Validation":
                                return props.configuration?.validationConfiguration?.tests || [];
                            case "PreSalesInsurance":
                                return props.configuration?.insuranceConfiguration?.tests || [];
                            case "FaultCheck":
                                return props.configuration?.ntfConfiguration?.tests || [];
                            default:
                                return [];
                        }
                    };

                    const handleTestsChange = (tests: Test[]) => {
                        const updates: Configuration = { ...props.configuration };
                        switch (featureKey) {
                            case "TradeIn":
                                updates.bbtiConfiguration = { ...(updates.bbtiConfiguration || {}), tests };
                                break;
                            case "Lease":
                                updates.leaseConfiguration = { ...(updates.leaseConfiguration || {}), tests };
                                break;
                            case "Validation":
                                updates.validationConfiguration = { ...(updates.validationConfiguration || {}), tests };
                                break;
                            case "PreSalesInsurance":
                                updates.insuranceConfiguration = { ...(updates.insuranceConfiguration || {}), tests };
                                break;
                            case "FaultCheck":
                                updates.ntfConfiguration = { ...(updates.ntfConfiguration || {}), tests };
                                break;
                        }
                        props.setFormValues(updates);
                    };

                    const formValues = {
                        brand: brand,
                        platform: platform,
                        componentFeatures: componentFeatures as ComponentFeatures,
                        bbtiConfiguration: props.configuration?.bbtiConfiguration || {},
                        leaseConfiguration: props.configuration?.leaseConfiguration || {},
                        validationConfiguration: props.configuration?.validationConfiguration || {},
                        insuranceConfiguration: props.configuration?.insuranceConfiguration || {},
                        ntfConfiguration: props.configuration?.ntfConfiguration || {},
                        identityVerificationConfiguration: props.configuration?.identityVerificationConfiguration || {},
                    };

                    const setFormValues = (
                        updater: typeof formValues | ((prev: typeof formValues) => typeof formValues)
                    ) => {
                        if (typeof updater === "function") {
                            const updated = updater(formValues);
                            const configUpdate: Configuration = { ...props.configuration };
                            if (updated.bbtiConfiguration) {
                                configUpdate.bbtiConfiguration = updated.bbtiConfiguration;
                            }
                            if (updated.leaseConfiguration) {
                                configUpdate.leaseConfiguration = updated.leaseConfiguration;
                            }
                            if (updated.validationConfiguration) {
                                configUpdate.validationConfiguration = updated.validationConfiguration;
                            }
                            if (updated.insuranceConfiguration) {
                                configUpdate.insuranceConfiguration = updated.insuranceConfiguration;
                            }
                            if (updated.ntfConfiguration) {
                                configUpdate.ntfConfiguration = updated.ntfConfiguration;
                            }
                            if (updated.identityVerificationConfiguration) {
                                configUpdate.identityVerificationConfiguration =
                                    updated.identityVerificationConfiguration;
                            }
                            props.setFormValues(configUpdate);
                        }
                    };

                    switch (featureKey) {
                        case "TradeIn":
                            return (
                                <BbtiConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        case "Lease":
                            return (
                                <LeaseConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        case "Validation":
                            return (
                                <ValidationConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        case "PreSalesInsurance":
                            return (
                                <InsuranceConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        case "FaultCheck":
                            return (
                                <NtfConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        case "infogathering":
                            return (
                                <IdentityVerificationConfigurationForm
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    showAdvanced={showAdvanced}
                                />
                            );
                        default:
                            return (
                                <DiagnosticsTestsForm
                                    currentEnabledTests={getConfigurationTests()}
                                    onTestChange={handleTestsChange}
                                    platform={platform}
                                />
                            );
                    }
                }
                return null;
        }
    };

    return (
        <>
            {props.allowAdvancedSettings === true && (
                <div>
                    <Checkbox
                        labelKey={t("Configuration.showAdvancedSettings")}
                        checkboxId="showAdvanced"
                        checked={showAdvanced}
                        onChange={setShowAdvanced}
                        className={classNames(style.nomargin)}
                    />
                </div>
            )}
            <div className={classNames(style.boxedSection)}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "16px",
                    }}
                >
                    <BmsSectionHeader
                        labelKey={getSectionHeader().labelKey}
                        toolTipContent={getSectionHeader().toolTipContent}
                    />
                    <div className={classNames(style.stepIndicatorContainer)}>
                        <div className={classNames(style.stepIndicatorDots)}>
                            {steps.map((_, index) => (
                                <div
                                    key={index}
                                    onClick={() => setCurrentStep(index)}
                                    className={classNames(style.stepDot, {
                                        [style.active]: index === currentStep,
                                        [style.completed]: index < currentStep,
                                    })}
                                    title={stepLabels[index]}
                                />
                            ))}
                        </div>
                        <div className={classNames(style.stepCounter)}>
                            Step {currentStep + 1} of {steps.length}
                        </div>
                    </div>
                </div>

                <div className={classNames(style.formSection, style.wizardField)}>
                    {renderCurrentStep()}

                    <div className={classNames(style.buttonContainer)}>
                        {currentStep > 0 && (
                            <button className={buttons.primaryButtonWithoutIcon} onClick={handlePrevious}>
                                Previous
                            </button>
                        )}
                        {currentStep < steps.length - 1 ? (
                            <button className={buttons.primaryButtonWithoutIcon} onClick={handleNext}>
                                Next
                            </button>
                        ) : (
                            <button
                                className={buttons.primaryButtonWithoutIcon}
                                onClick={props.onSave}
                                disabled={!canSave}
                            >
                                Save
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default connector(BmsWizard);
